import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  DateField,
  Notice,
  Segmented,
  SelectField,
  TextArea,
  TextField,
  useSnackbar,
} from '@sauda/ui/kit';
import { useAuth } from '../auth/AuthContext.js';
import { ApiError, api } from '../lib/api.js';
import { parsePastedTenancies, type FlatChoice } from '../lib/backfill-paste.js';
import { rupeesToPaise } from '../lib/money.js';
import type { PropertyView } from '../lib/types.js';

interface Result {
  created: number;
  written: boolean;
  results: { index: number; ok: boolean; error: string | null }[];
}

/**
 * Add tenancies that are already running so the renewal radar has real dates and money from the
 * first day: one at a time, or up to 50 pasted from Excel, checked before anything is saved.
 */
export function OldTenancySheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [mode, setMode] = useState<'one' | 'paste'>('one');
  const [flat, setFlat] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [rent, setRent] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [brokerage, setBrokerage] = useState('');
  const [paste, setPaste] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const properties = useQuery({
    queryKey: ['properties', auth.agencyId],
    queryFn: () => api<PropertyView[]>('/properties'),
    enabled: open,
  });
  const flats: FlatChoice[] = useMemo(
    () =>
      (properties.data ?? []).map((p) => ({
        id: p.id,
        title: p.title,
        society: p.society,
        flatNo: p.flatNo,
      })),
    [properties.data],
  );
  const parsed = useMemo(() => parsePastedTenancies(paste, flats), [paste, flats]);
  const send = useMutation({
    mutationFn: (v: { rows: unknown[]; dryRun: boolean }) =>
      api<Result>('/deals/backfill', { method: 'POST', body: v }),
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status < 500 && e.message !== ''
          ? e.message
          : t('oldTenancy.failed'),
      ),
  });

  const rowFromOne = () => {
    const rentPaise = rupeesToPaise(rent);
    return {
      tenantName: tenantName.trim(),
      tenantPhone: tenantPhone.trim(),
      ownerName: ownerName.trim() === '' ? null : ownerName.trim(),
      ownerPhone: ownerPhone.trim() === '' ? null : ownerPhone.trim(),
      propertyId: flat,
      rentPaise: rentPaise ?? 0,
      startDate: start,
      endDate: end,
      brokeragePaise: brokerage.trim() === '' ? null : rupeesToPaise(brokerage),
    };
  };
  const rows =
    mode === 'one' ? [rowFromOne()] : parsed.flatMap((p) => (p.row === null ? [] : [p.row]));
  const pasteErrors = mode === 'paste' ? parsed.filter((p) => p.error !== null) : [];
  const ready =
    mode === 'one'
      ? flat !== '' &&
        tenantName.trim() !== '' &&
        tenantPhone.trim() !== '' &&
        rent !== '' &&
        start !== '' &&
        end !== ''
      : rows.length > 0 && pasteErrors.length === 0;

  const run = async (dryRun: boolean) => {
    setError(null);
    setProblems([]);
    const result = await send.mutateAsync({ rows, dryRun });
    const bad = result.results.filter((r) => !r.ok);
    if (bad.length > 0) {
      setProblems(bad.map((r) => `${t('oldTenancy.row', { n: r.index + 1 })}: ${r.error ?? ''}`));
      return;
    }
    if (dryRun) {
      snack({ text: t('oldTenancy.checked', { count: rows.length }) });
      return;
    }
    await Promise.all([
      cache.invalidateQueries({ queryKey: ['renewals'] }),
      cache.invalidateQueries({ queryKey: ['today'] }),
    ]);
    snack({ text: t('oldTenancy.added', { count: result.created }) });
    setPaste('');
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      label={t('oldTenancy.title')}
      title={t('oldTenancy.title')}
      closeLabel={t('app.close')}
      actions={
        <div className="chips-row">
          <Button
            variant="tonal"
            size="lg"
            disabled={!ready}
            loading={send.isPending}
            onClick={() => void run(true)}
          >
            {t('oldTenancy.check')}
          </Button>
          <Button
            size="lg"
            disabled={!ready}
            loading={send.isPending}
            onClick={() => void run(false)}
          >
            {t('oldTenancy.add', { count: rows.length })}
          </Button>
        </div>
      }
    >
      <div className="k-stack">
        <p className="k-t-body-sm k-muted">{t('oldTenancy.help')}</p>
        <span className="k-t-label-lg">{t('oldTenancy.mode')}</span>
        <Segmented
          label={t('oldTenancy.mode')}
          value={mode}
          onChange={(v) => setMode(v as 'one' | 'paste')}
          options={[
            { value: 'one', label: t('oldTenancy.one') },
            { value: 'paste', label: t('oldTenancy.paste') },
          ]}
        />
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        {problems.length === 0 ? null : (
          <Notice tone="error" title={t('oldTenancy.problems')}>
            <ul>
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Notice>
        )}
        {mode === 'one' ? (
          <>
            <SelectField
              label={t('oldTenancy.flat')}
              value={flat}
              placeholder={t('enquiry.noFlat')}
              onChange={(e) => setFlat(e.target.value)}
              options={flats.map((f) => ({ value: f.id, label: f.title }))}
            />
            <TextField
              label={t('oldTenancy.tenant')}
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
            />
            <TextField
              label={t('oldTenancy.tenantPhone')}
              type="tel"
              inputMode="numeric"
              prefix="+91"
              value={tenantPhone}
              onChange={(e) => setTenantPhone(e.target.value)}
            />
            <TextField
              label={t('oldTenancy.owner')}
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
            <TextField
              label={t('oldTenancy.ownerPhone')}
              type="tel"
              inputMode="numeric"
              prefix="+91"
              value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value)}
            />
            <TextField
              label={t('oldTenancy.rent')}
              prefix="₹"
              inputMode="decimal"
              value={rent}
              onChange={(e) => setRent(e.target.value)}
            />
            <DateField label={t('oldTenancy.start')} value={start} onChange={setStart} />
            <DateField label={t('oldTenancy.end')} value={end} onChange={setEnd} />
            <TextField
              label={t('oldTenancy.brokerage')}
              prefix="₹"
              inputMode="decimal"
              value={brokerage}
              onChange={(e) => setBrokerage(e.target.value)}
            />
          </>
        ) : (
          <>
            <p className="k-t-body-sm">{t('oldTenancy.columns')}</p>
            <TextArea
              label={t('oldTenancy.pasteBox')}
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              rows={8}
            />
            {pasteErrors.length === 0 ? (
              rows.length === 0 ? null : (
                <p className="k-t-body-sm">{t('oldTenancy.rowsReady', { count: rows.length })}</p>
              )
            ) : (
              <Notice tone="error" title={t('oldTenancy.problems')}>
                <ul>
                  {pasteErrors.map((p) => (
                    <li key={p.line}>
                      {t('oldTenancy.line', { n: p.line })}: {p.error}
                    </li>
                  ))}
                </ul>
              </Notice>
            )}
          </>
        )}
      </div>
    </BottomSheet>
  );
}
